import {
  isTerminalOptimizationJobStatus,
  isSucceededOptimizationJobStatus,
  isFailedOptimizationJobStatus,
  normalizeOptimizationJobStatus,
} from './optimizationJobLifecycle';
import { canRetryOptimizationJob, isActiveJobStatus } from './trainingJobRetry';
import {
  isOptimizationRunnerProgressComplete,
  parseOptimizationJobRunnerProgress,
  resolveOptimizationJobRunnerProgressDisplay,
} from './optimizationJobRunnerProgress';

/** Unwrap list/get job rows that may be nested under `.data`. */
export function unwrapOptimizationJobRow(row: unknown): Record<string, unknown> {
  if (!row || typeof row !== 'object') return {};
  let cur = row as Record<string, unknown>;
  for (let depth = 0; depth < 3; depth += 1) {
    const inner = cur.data;
    if (!inner || typeof inner !== 'object' || Array.isArray(inner)) break;
    const record = inner as Record<string, unknown>;
    if (
      'id' in record ||
      'status' in record ||
      'progress_percent' in record ||
      'steps' in record ||
      'runner_progress' in record
    ) {
      cur = record;
    } else {
      break;
    }
  }
  return cur;
}

/** Job UUID for optimize/jobs endpoints — never use optimization_run_id here. */
export function resolveOptimizationJobRecordId(job: unknown): string {
  const row = unwrapOptimizationJobRow(job);
  const id = String(row.id ?? '').trim();
  if (id) return id;
  return String(row.job_id ?? '').trim();
}

function optimizationJobRetrySignals(job: Record<string, unknown>): string[] {
  const status = normalizeOptimizationJobStatus(String(job.status || ''));
  const phase = normalizeOptimizationJobStatus(String(job.phase || ''));
  return [status, phase].filter(Boolean);
}

/** Whether POST /auto-tune/optimize/jobs/{id}/retry is expected to succeed for this row. */
export function isOptimizationJobRetryEligible(job: unknown): boolean {
  const row = unwrapOptimizationJobRow(job);
  return optimizationJobRetrySignals(row).some((value) => canRetryOptimizationJob(value));
}

function readNumericProgress(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = typeof value === 'string' ? parseFloat(value) : Number(value);
  if (!Number.isFinite(n)) return undefined;
  return Math.max(0, Math.min(100, n));
}

function readPositiveInt(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = typeof value === 'string' ? parseInt(value, 10) : Number(value);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.floor(n);
}

/**
 * For N requested optimization iterations the backend emits step_number 0..N
 * (baseline + N optimization steps). API `current_step` is 1-based
 * (current_step 1 → step_number 0; current_step 5 → step_number 4 for N=4).
 * Progress divides 100% across N + 1 steps (20% each when N=4).
 */
export function resolveOptimizationJobExpectedMaxStepNumber(iterations: number): number {
  const n = Math.floor(iterations);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n;
}

export function resolveOptimizationJobExpectedStepCount(iterations: number): number {
  const n = Math.floor(iterations);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n + 1;
}

/** Jobs API uses 1-based step_number/current_step unless a step_number 0 is present. */
export function isOptimizationJobStepNumberOneBased(steps: unknown[]): boolean {
  if (!Array.isArray(steps) || steps.length === 0) return true;

  for (const step of steps) {
    if (!step || typeof step !== 'object') continue;
    const s = step as Record<string, unknown>;
    if (s.step_number === 0 || s.seq === 0) return false;
  }

  return true;
}

/** Map API step_number/current_step to 0-based graph index (current_step 1 → 0). */
export function normalizeOptimizationJobStepIndex(
  rawStepNumber: number | undefined,
  steps?: unknown[]
): number {
  if (rawStepNumber == null || !Number.isFinite(rawStepNumber)) return 0;
  const n = Math.floor(rawStepNumber);
  if (n <= 0) return 0;

  const oneBased = isOptimizationJobStepNumberOneBased(steps ?? []);
  return oneBased ? n - 1 : n;
}

export function resolveOptimizationJobStepDisplayIndex(step: unknown, job?: unknown): number {
  if (!step || typeof step !== 'object') return 0;
  const s = step as Record<string, unknown>;
  if (typeof s.seq === 'number') return s.seq;

  const row = job ? unwrapOptimizationJobRow(job) : {};
  const steps = Array.isArray(row.steps) ? row.steps : [];
  const raw =
    typeof s.step_number === 'number'
      ? s.step_number
      : typeof s.iteration === 'number'
        ? s.iteration
        : 0;

  return normalizeOptimizationJobStepIndex(raw, steps);
}

/** Completed steps for progress (1 when baseline/current_step 1 arrived, etc.). */
export function resolveOptimizationJobCompletedStepCount(job: unknown): number {
  const row = unwrapOptimizationJobRow(job);
  const steps = Array.isArray(row.steps) ? row.steps : [];
  if (steps.length > 0) return steps.length;

  const currentStep = readPositiveInt(row.current_step);
  if (currentStep != null && currentStep > 0) return currentStep;

  return 0;
}

export function resolveOptimizationJobIterationCount(
  job: unknown,
  fallbackIterations?: number
): number | undefined {
  const row = unwrapOptimizationJobRow(job);
  return (
    readNumericProgress(row.iterations) ??
    readNumericProgress(row.total_iterations) ??
    (fallbackIterations != null && fallbackIterations > 0 ? fallbackIterations : undefined)
  );
}

export function resolveOptimizationJobMaxStepNumber(job: unknown): number {
  const row = unwrapOptimizationJobRow(job);
  const steps = Array.isArray(row.steps) ? row.steps : [];
  let maxStep = 0;

  const currentStep = readPositiveInt(row.current_step);
  if (currentStep != null && currentStep > 0) {
    maxStep = Math.max(maxStep, currentStep - 1);
  }

  for (const step of steps) {
    if (!step || typeof step !== 'object') continue;
    const sn = resolveOptimizationJobStepDisplayIndex(step, row);
    if (sn > maxStep) maxStep = sn;
  }
  return maxStep;
}

export function isOptimizationJobStepsComplete(job: unknown, fallbackIterations?: number): boolean {
  if (isOptimizationRunnerProgressComplete(parseOptimizationJobRunnerProgress(job))) {
    return true;
  }

  const totalIterations = resolveOptimizationJobIterationCount(job, fallbackIterations);
  if (totalIterations == null || totalIterations <= 0) return false;

  const expectedMaxStep = resolveOptimizationJobExpectedMaxStepNumber(totalIterations);
  const expectedStepCount = resolveOptimizationJobExpectedStepCount(totalIterations);
  const completedSteps = resolveOptimizationJobCompletedStepCount(job);

  if (completedSteps >= expectedStepCount) return true;
  return resolveOptimizationJobMaxStepNumber(job) >= expectedMaxStep;
}

/** Prefer terminal signals from status, phase, timestamps, and progress fields. */
export function resolveEffectiveOptimizationJobStatus(job: unknown): string {
  const row = unwrapOptimizationJobRow(job);
  const status = normalizeOptimizationJobStatus(String(row.status || ''));
  const phase = normalizeOptimizationJobStatus(String(row.phase || ''));

  if (isTerminalOptimizationJobStatus(status)) return status;
  if (isTerminalOptimizationJobStatus(phase)) return phase;
  if (isSucceededOptimizationJobStatus(phase)) return 'succeeded';
  if (isFailedOptimizationJobStatus(phase)) return 'failed';

  const completedAt = row.completed_at ?? row.finished_at;
  if (typeof completedAt === 'string' && completedAt.trim()) {
    if (isFailedOptimizationJobStatus(status) || isFailedOptimizationJobStatus(phase)) return 'failed';
    if (String(row.error || '').trim()) return 'failed';
    return 'succeeded';
  }

  const progress =
    readNumericProgress(row.progress_percent) ??
    readNumericProgress(row.progress) ??
    readNumericProgress(row.progressPercent);
  if (progress != null && progress >= 100) {
    if (String(row.error || '').trim()) return 'failed';
    return 'succeeded';
  }

  return status || phase || '';
}

/** Infer terminal status when API leaves the job "running" after all steps finished. */
export function inferOptimizationJobTerminalStatus(
  job: unknown,
  fallbackIterations?: number
): 'succeeded' | 'failed' | null {
  const row = unwrapOptimizationJobRow(job);
  if (String(row.error || '').trim()) return 'failed';

  const effective = resolveEffectiveOptimizationJobStatus(job);
  if (isTerminalOptimizationJobStatus(effective)) {
    return isFailedOptimizationJobStatus(effective) ? 'failed' : 'succeeded';
  }

  if (isOptimizationRunnerProgressComplete(parseOptimizationJobRunnerProgress(job))) {
    return 'succeeded';
  }

  if (isOptimizationJobStepsComplete(job, fallbackIterations)) {
    return 'succeeded';
  }

  const progress =
    readNumericProgress(row.progress_percent) ??
    readNumericProgress(row.progress) ??
    readNumericProgress(row.progressPercent);
  if (progress != null && progress >= 100) return 'succeeded';

  if (row.completed_at || row.finished_at) return 'succeeded';
  if (
    row.current_config &&
    typeof row.current_config === 'object' &&
    isOptimizationJobStepsComplete(job, fallbackIterations)
  ) {
    return 'succeeded';
  }

  return null;
}

/** Merge list/poll rows with derived status + progress for consistent UI. */
export function enrichOptimizationJobRowForDisplay(
  job: unknown,
  fallbackIterations?: number
): Record<string, unknown> {
  const row = unwrapOptimizationJobRow(job);
  const effectiveStatus = resolveEffectiveOptimizationJobStatus(row);
  const enriched = {
    ...row,
    ...(effectiveStatus ? { status: effectiveStatus } : {}),
  };
  const progress = resolveOptimizationJobProgressPercent(enriched, fallbackIterations);
  return {
    ...enriched,
    progress_percent: progress,
    progress: progress,
  };
}

export function mergeOptimizationJobHistoryRows(
  listRows: unknown[],
  liveById: ReadonlyMap<string, Record<string, unknown>>,
  fallbackIterations?: number
): Record<string, unknown>[] {
  return (Array.isArray(listRows) ? listRows : []).map((row) => {
    const base = unwrapOptimizationJobRow(row);
    const id = resolveOptimizationJobRecordId(base);
    const live = id ? liveById.get(id) : undefined;
    return enrichOptimizationJobRowForDisplay(live ? { ...base, ...live } : base, fallbackIterations);
  });
}

/** Whether polling should continue — uses effective status, not raw `running` at 99%. */
export function shouldTrackOptimizationJobRow(job: unknown): boolean {
  const effective = resolveEffectiveOptimizationJobStatus(job);
  if (isTerminalOptimizationJobStatus(effective)) return false;
  return isActiveJobStatus(effective);
}

/** Resolve display progress from API fields or completed optimization steps. */
export function resolveOptimizationJobProgressPercent(
  job: unknown,
  fallbackIterations?: number
): number {
  const row = unwrapOptimizationJobRow(job);
  const status = resolveEffectiveOptimizationJobStatus(job);

  if (isSucceededOptimizationJobStatus(status)) return 100;
  if (isTerminalOptimizationJobStatus(status) && !isSucceededOptimizationJobStatus(status)) {
    const terminalProgress =
      readNumericProgress(row.progress_percent) ??
      readNumericProgress(row.progress) ??
      readNumericProgress(row.progressPercent);
    if (terminalProgress != null) return Math.round(terminalProgress);
  }

  const fromApi =
    readNumericProgress(row.progress_percent) ??
    readNumericProgress(row.progress) ??
    readNumericProgress(row.progressPercent);

  if (fromApi != null) {
    const rounded = Math.round(fromApi);
    if (isActiveJobStatus(status)) return Math.min(99, rounded);
    return Math.min(100, rounded);
  }

  const totalIterations = resolveOptimizationJobIterationCount(job, fallbackIterations);

  if (totalIterations != null && totalIterations > 0) {
    const expectedStepCount = resolveOptimizationJobExpectedStepCount(totalIterations);
    const completedSteps = resolveOptimizationJobCompletedStepCount(job);
    if (completedSteps > 0 && expectedStepCount > 0) {
      const pct = Math.round((completedSteps / expectedStepCount) * 100);
      if (isActiveJobStatus(status) && completedSteps < expectedStepCount) {
        return Math.min(99, pct);
      }
      return Math.min(100, pct);
    }
  }

  if (totalIterations != null && totalIterations > 0) {
    const maxStep = resolveOptimizationJobMaxStepNumber(job);
    const expectedMaxStep = resolveOptimizationJobExpectedMaxStepNumber(totalIterations);
    if (maxStep >= 0 && expectedMaxStep > 0) {
      const pct = Math.round(((maxStep + 1) / (expectedMaxStep + 1)) * 100);
      if (isActiveJobStatus(status)) return Math.min(99, pct);
      return Math.min(100, pct);
    }
  }

  return 0;
}

export function resolveOptimizationJobProgressDisplay(
  job: unknown,
  fallbackIterations?: number
): { percent: number; message: string | null; meta: string | null } {
  const row = unwrapOptimizationJobRow(job);
  const status = normalizeOptimizationJobStatus(String(row.status || ''));
  const percent = resolveOptimizationJobProgressPercent(job, fallbackIterations);
  const { message, meta } = resolveOptimizationJobRunnerProgressDisplay(job);
  if (status === 'cancelling') {
    return { percent, message: 'Cancelling…', meta };
  }
  return { percent, message, meta };
}
